import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../modules/auth/hooks/useAuth';
import { LoggerService } from '../../shared/services/logger.service';

interface UserDropdownMenuProps {
  onNavigate?: (section: string) => void;
}

export const UserDropdownMenu: React.FC<UserDropdownMenuProps> = ({ onNavigate }) => {
  const { user, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Cerrar dropdown cuando se hace click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    try {
      LoggerService.info('User logout initiated', 'UserDropdownMenu', { email: user?.email });
      await logout();
      setIsOpen(false);
    } catch (error) {
      LoggerService.error('Logout failed', 'UserDropdownMenu', { error });
    }
  };

  const menuItems = [
    {
      icon: 'ri-user-3-line',
      label: 'Mi Perfil',
      action: () => {
        LoggerService.info('Profile menu clicked', 'UserDropdownMenu');
        onNavigate?.('profile');
        setIsOpen(false);
      }
    },
    {
      icon: 'ri-settings-3-line',
      label: 'Configuración',
      action: () => {
        LoggerService.info('Settings menu clicked', 'UserDropdownMenu');
        onNavigate?.('settings');
        setIsOpen(false);
      }
    },
    {
      icon: 'ri-notification-3-line',
      label: 'Notificaciones',
      action: () => {
        LoggerService.info('Notifications menu clicked', 'UserDropdownMenu');
        // TODO: Implementar página de notificaciones
        setIsOpen(false);
      }
    },
    {
      icon: 'ri-shield-check-line',
      label: 'Seguridad',
      action: () => {
        LoggerService.info('Security menu clicked', 'UserDropdownMenu');
        onNavigate?.('security');
        setIsOpen(false);
      }
    },
    {
      icon: 'ri-file-text-line',
      label: 'Facturación',
      action: () => {
        LoggerService.info('Billing menu clicked', 'UserDropdownMenu');
        // TODO: Implementar página de facturación
        setIsOpen(false);
      }
    },
    {
      icon: 'ri-question-line',
      label: 'Ayuda',
      action: () => {
        LoggerService.info('Help menu clicked', 'UserDropdownMenu');
        // TODO: Implementar página de ayuda
        setIsOpen(false);
      }
    }
  ];

  if (!user) {
    return null;
  }

  // Obtener iniciales del usuario
  const getInitials = (email: string) => {
    return email.charAt(0).toUpperCase();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* User Avatar Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
      >
        {/* Avatar */}
        <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white font-medium text-sm">
          {getInitials(user.email || 'U')}
        </div>
        
        {/* User Info */}
        <div className="hidden md:block text-left">
          <div className="text-sm font-medium text-gray-900">
            {user.email?.split('@')[0] || 'Usuario'}
          </div>
          <div className="text-xs text-gray-500">
            Plan Premium
          </div>
        </div>
        
        {/* Dropdown Arrow */}
        <i className={`ri-arrow-down-s-line text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}></i>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
          {/* User Info Header */}
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center text-white font-medium">
                {getInitials(user.email || 'U')}
              </div>
              <div>
                <div className="font-medium text-gray-900">
                  {user.email?.split('@')[0] || 'Usuario'}
                </div>
                <div className="text-sm text-gray-500">
                  {user.email}
                </div>
                <div className="text-xs text-emerald-600 font-medium">
                  Plan Premium
                </div>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-2">
            {menuItems.map((item, index) => (
              <button
                key={index}
                onClick={item.action}
                className="w-full flex items-center space-x-3 px-4 py-2 text-left hover:bg-gray-50 transition-colors"
              >
                <i className={`${item.icon} text-gray-400 text-lg`}></i>
                <span className="text-sm text-gray-700">{item.label}</span>
              </button>
            ))}
          </div>

          {/* Logout Button */}
          <div className="border-t border-gray-100 pt-2">
            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-3 px-4 py-2 text-left hover:bg-red-50 transition-colors text-red-600"
            >
              <i className="ri-logout-box-r-line text-lg"></i>
              <span className="text-sm font-medium">Cerrar Sesión</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};